export interface AssistantStepConfig {
  title: string;
  suggestedQuestions: string[];
  introMessage?: string;
  placeholder?: string;
  /** Persona/instructions sent to the model with every message — this is what
   * actually makes the assistant's behavior adapt per stage, not just its UI. */
  systemPrompt?: string;
}

// Shared verbatim between Extraction and Quality Assurance per the ticket's spec table.
const EXTRACTION_QA_QUICK_ACTIONS: string[] = [
  "Explain what 'Primary Outcomes' should include",
  "Are there inconsistencies in the extracted data?",
  "How should I handle missing population data?",
  "Compare efficacy results across studies",
  "Flag any data points that look inconsistent",
  "What validation criteria should I apply?",
  "Which rows have the most missing data?",
  "Assess the overall extraction quality",
];

// Appended to every persona — the chat can't edit the project itself, so it
// must never imply that it has (matches the ticket's example interaction:
// the analyst accepts a suggestion and applies it manually).
const ADVISORY_ONLY_NOTE =
  " You cannot edit the project yourself — describe what the user should change, and they will apply it manually in the workflow.";

// Per-step assistant persona for @lumanity/ui/chat's AssistantWidget — title,
// quick-action prompts, opening message, and input placeholder, keyed by
// workflow step (see config/projectTypeConfig.ts for the step keys themselves).
export const ASSISTANT_BY_STEP: Record<string, AssistantStepConfig> = {
  "Evidence Gathering": {
    title: "Evidence Assistant",
    suggestedQuestions: [
      "Summarise the key findings across my documents",
      "Which studies are most relevant to this project?",
      "Are there any gaps in my evidence base?",
      "What is the overall quality of this evidence?",
    ],
    introMessage:
      "I can help you review your evidence base — summarise findings across documents, flag gaps, and assess overall quality.",
    placeholder: "Ask about your evidence base…",
    systemPrompt:
      "You are the Evidence Assistant, helping the user review and evaluate their project's evidence base. " +
      "Help summarise findings across documents, identify the most relevant studies, spot gaps in the evidence " +
      "base, and assess the overall quality of the evidence." + ADVISORY_ONLY_NOTE,
  },
  "Extraction": {
    title: "Extraction Assistant",
    suggestedQuestions: EXTRACTION_QA_QUICK_ACTIONS,
    introMessage:
      "I can help you interpret extracted data, clarify field definitions, identify inconsistencies, or suggest how to handle missing values across studies.",
    placeholder: "Ask about extracted data…",
    systemPrompt:
      "You are the Extraction Assistant, helping the user interpret extracted data. Help clarify field " +
      "definitions, identify inconsistencies across studies, and suggest how to handle missing values." +
      ADVISORY_ONLY_NOTE,
  },
  "Outline Creation": {
    title: "Outline Assistant",
    suggestedQuestions: [
      "What should the Executive Summary include?",
      "Suggest content for the Safety section",
      "How should I order my clinical sections?",
      "@ a section to ask about it specifically",
    ],
    introMessage:
      "I can help you structure your report outline, suggest section content, or advise on what evidence to include. Use @ to reference a specific section.",
    placeholder: "Ask about your outline, or type @ to reference a section…",
    systemPrompt:
      "You are the Outline Assistant, helping the user structure their report outline. Help suggest section " +
      "content, advise on what evidence to include, and discuss specific report sections when the user " +
      "references one." + ADVISORY_ONLY_NOTE,
  },
  "Quality Assurance": {
    title: "QA Assistant",
    suggestedQuestions: EXTRACTION_QA_QUICK_ACTIONS,
    introMessage:
      "I can help you validate extracted data, flag potential errors, assess inter-rater reliability, or suggest quality improvement actions.",
    placeholder: "Ask about validation or data quality…",
    systemPrompt:
      "You are the QA Assistant, helping the user validate extracted data. Help flag potential errors, assess " +
      "inter-rater reliability, and suggest quality improvement actions." + ADVISORY_ONLY_NOTE,
  },
  "Report Writing": {
    title: "Report Writing Assistant",
    suggestedQuestions: [
      "Suggest an opening paragraph for this section",
      "Make this section more concise",
      "Check consistency across sections",
      "@ a section to ask about its content",
    ],
    introMessage:
      "I can help you draft content, refine language, ensure consistency, or generate citations. Type @ to reference a specific report section and ask questions about it.",
    placeholder: "Ask about this report, or type @ to reference a section…",
    systemPrompt:
      "You are the Writing Assistant, helping the user draft and refine report content. Help draft content, " +
      "refine language, ensure consistency across sections, generate citations, and discuss specific report " +
      "sections when the user references one." + ADVISORY_ONLY_NOTE,
  },
};

// Per-(project type, step) overrides — only populate a stage here once its
// content actually needs to diverge between GVD and PICO. Today it's empty:
// PICO only has stages that are identical to GVD's per the ticket's spec
// table, so everything falls back to ASSISTANT_BY_STEP. This exists so that
// divergence can be expressed later without restructuring the lookup.
const ASSISTANT_OVERRIDES_BY_PROJECT_TYPE: Partial<Record<string, Partial<Record<string, AssistantStepConfig>>>> = {};

export function getAssistantStepConfig(
  stepKey: string,
  projectType?: string | null,
): AssistantStepConfig | undefined {
  const override = projectType
    ? ASSISTANT_OVERRIDES_BY_PROJECT_TYPE[projectType.toUpperCase()]?.[stepKey]
    : undefined;
  return override ?? ASSISTANT_BY_STEP[stepKey];
}
